import { useCallback, useEffect, useRef, useState } from "react";

import { useKeycloak } from "@king-ict/eupisi-ui/hooks";
import { useDispatch } from "react-redux";

import { useTypedSelector } from "../../app";
import { isUserAuthorized, UserAuthData } from "../../models/User";
import { getUserAuthData, updateUserAuthorization } from "../../slices";

export const useAuthorization = (shouldInitialize: boolean) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const setInitializedFlagAfterAuthorization = useRef(false);

  const { keycloak } = useKeycloak();
  const dispatch = useDispatch();

  const authData = useTypedSelector((s) => s.application.userAuthData);
  const user = useTypedSelector((s) => s.application.user);
  const sessionIdCookieName = "sessionId";
  const authDataIdCookieName = "authDataId";

  useEffect(() => {
    if (shouldInitialize) {
      dispatch(getUserAuthData());
    }
  }, [shouldInitialize, dispatch]);

  useEffect(() => {
    if (isUserAuthorized(user)) {
      setIsAuthorized(true);

      if (setInitializedFlagAfterAuthorization.current) {
        setIsInitialized(true);
      }
    }
  }, [user]);

  const getFromCookie = (): {
    sessionId: string | undefined;
    authDataId: number | undefined;
  } => {
    const cookies = document.cookie.split(";");

    let sessionId: string | undefined = undefined;
    let authDataId: number | undefined = undefined;

    cookies.forEach((c) => {
      const cookie = c.trim();
      const name = cookie.split("=")[0];
      const value = cookie.split("=")[1];

      switch (name) {
        case sessionIdCookieName:
          sessionId = value;
          break;
        case authDataIdCookieName:
          authDataId = +value;
          break;
      }
    });

    return { authDataId: authDataId, sessionId: sessionId };
  };

  const saveCookie = (
    cookieName: string,
    cookieValue: string
  ) => {
    const cookieDomain = process.env.REACT_APP_AUTH_COOKIE_DOMAIN;

    let cookieString = `${cookieName}=${cookieValue};domain=${cookieDomain};path=/;`;

    if (process.env.NODE_ENV === 'development'){
      cookieString += 'SameSite=None;Secure;'
    }

    document.cookie = cookieString;
  };

  const saveToCookie = (
    authDataItem: UserAuthData,
    sessionId: string | undefined
  ) => {
    if (sessionId) {
      saveCookie(sessionIdCookieName, sessionId)
    }
    saveCookie(authDataIdCookieName, authDataItem.id.toString())
  };

  const updateUserAuthData = useCallback(
    (authDataItem: UserAuthData) => {
      saveToCookie(authDataItem, keycloak.sessionId);
      dispatch(updateUserAuthorization(authDataItem));
    },
    [dispatch, keycloak.sessionId]
  );

  const initialize = useCallback(() => {
    if (isInitialized || keycloak?.sessionId == null || authData == null) {
      return;
    }

    let authDataItem: UserAuthData | undefined = undefined;

    const { sessionId, authDataId } = getFromCookie();
    if (authDataId && sessionId === keycloak.sessionId) {
      authDataItem = authData.find((a) => a.id === authDataId);
    }

    if (authDataItem == null && authData.length === 1) {
      authDataItem = authData[0];
    }

    if (authDataItem != null) {
      updateUserAuthData(authDataItem);
      setInitializedFlagAfterAuthorization.current = true;
    } else {
      setIsInitialized(true);
    }
  }, [isInitialized, keycloak.sessionId, authData, updateUserAuthData]);

  useEffect(() => {
    if (shouldInitialize) {
      initialize();
    }
  }, [shouldInitialize, initialize]);

  return { isInitialized, isAuthorized, updateUserAuthData };
};
